import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { pcmpPerformanceReportQueryOptions } from '../api/pcmpReports.api';
import { PcmpPrintSheet } from '../components/PcmpPrint/PcmpPrintSheet';
import { PcmpPerformanceReportSheet } from '../components/PcmpReports/PcmpPerformanceReportSheet';
import type { PcmpReportFilters } from '../types/pcmpReports.types';

const route = getRouteApi('/_print/pcmp/reports/performance/print');

/** The Performance report on landscape A4 sheets, opening the print dialog once loaded. */
export function PcmpPerformanceReportPrintPage() {
  const { projectId, division, departmentId, dateFrom, dateTo } = route.useSearch();

  const params = useMemo<PcmpReportFilters>(
    () => ({ projectId, division, departmentId, dateFrom, dateTo }),
    [projectId, division, departmentId, dateFrom, dateTo],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    ...pcmpPerformanceReportQueryOptions(params),
    // Closing the print dialog refocuses the tab; keep the sheet as printed.
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data) document.title = `PCMP Performance Report ${data.reportDate}`;
  }, [data]);

  if (isLoading) return <PageLoader />;

  if (error || !data) {
    return (
      <div className="p-6">
        <ErrorState
          error={error}
          fallbackTitle="Could not load the Performance report"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  return (
    <PcmpPrintSheet>
      <PcmpPerformanceReportSheet report={data} />
    </PcmpPrintSheet>
  );
}
